import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  CatalogStats,
  ImagePreviewType,
  ProductCategory,
  ProductDetail,
  ProductListItem,
  ProductModalMode,
} from "@/components/ProductCatalogParent/types";

// ============================================================================
// State Interface
// ============================================================================

interface CentralProductCatalogState {
  // Stats/KPI
  stats: CatalogStats | null;

  // Products list
  products: ProductListItem[];
  productsPagination: { count: number; page: number; pageSize: number };

  // Product detail modal
  detailModal: {
    open: boolean;
    productId: string | null;
  };
  selectedProduct: ProductDetail | null;
  selectedProductLoading: boolean;
  selectedProductError: string | null;

  // Product form modal (Add/Edit)
  formModal: {
    open: boolean;
    mode: ProductModalMode;
    editData: ProductDetail | null;
  };

  // Categories modal
  categoriesModal: {
    open: boolean;
  };
  categories: ProductCategory[];

  // Image preview modal
  imagePreviewModal: {
    open: boolean;
    imageType: ImagePreviewType | null;
    imageUrl: string | null;
  };
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: CentralProductCatalogState = {
  stats: null,
  products: [],
  productsPagination: { count: 0, page: 1, pageSize: 10 },
  detailModal: { open: false, productId: null },
  selectedProduct: null,
  selectedProductLoading: false,
  selectedProductError: null,
  formModal: { open: false, mode: "create", editData: null },
  categoriesModal: { open: false },
  categories: [],
  imagePreviewModal: { open: false, imageType: null, imageUrl: null },
};

// ============================================================================
// Slice
// ============================================================================

const centralProductCatalogSlice = createSlice({
  name: "centralProductCatalog",
  initialState,
  reducers: {
    // Stats
    setStats: (state, action: PayloadAction<CatalogStats>) => {
      state.stats = action.payload;
    },

    // Products list
    setProducts: (state, action: PayloadAction<ProductListItem[]>) => {
      state.products = action.payload;
    },
    setProductsPagination: (
      state,
      action: PayloadAction<{
        count: number;
        page: number;
        pageSize: number;
      }>,
    ) => {
      state.productsPagination = action.payload;
    },

    // Detail modal
    openDetailModal: (state, action: PayloadAction<string>) => {
      state.detailModal = { open: true, productId: action.payload };
    },
    closeDetailModal: (state) => {
      state.detailModal = { open: false, productId: null };
      state.selectedProduct = null;
      state.selectedProductLoading = false;
      state.selectedProductError = null;
    },
    setSelectedProduct: (
      state,
      action: PayloadAction<ProductDetail | null>,
    ) => {
      state.selectedProduct = action.payload;
    },
    setSelectedProductLoading: (state, action: PayloadAction<boolean>) => {
      state.selectedProductLoading = action.payload;
    },
    setSelectedProductError: (state, action: PayloadAction<string | null>) => {
      state.selectedProductError = action.payload;
    },

    // Form modal (Add/Edit)
    openFormModal: (
      state,
      action: PayloadAction<{
        mode: ProductModalMode;
        editData?: ProductDetail | null;
      }>,
    ) => {
      state.formModal = {
        open: true,
        mode: action.payload.mode,
        editData: action.payload.editData ?? null,
      };
    },
    closeFormModal: (state) => {
      state.formModal = { open: false, mode: "create", editData: null };
    },

    // Categories modal
    openCategoriesModal: (state) => {
      state.categoriesModal = { open: true };
    },
    closeCategoriesModal: (state) => {
      state.categoriesModal = { open: false };
    },
    setCategories: (state, action: PayloadAction<ProductCategory[]>) => {
      state.categories = action.payload;
    },
    addCategory: (state, action: PayloadAction<ProductCategory>) => {
      state.categories.push(action.payload);
    },
    updateCategory: (state, action: PayloadAction<ProductCategory>) => {
      const index = state.categories.findIndex(
        (c) => c.id === action.payload.id,
      );
      if (index !== -1) {
        state.categories[index] = action.payload;
      }
    },
    removeCategory: (state, action: PayloadAction<string>) => {
      state.categories = state.categories.filter(
        (c) => c.id !== action.payload,
      );
    },

    // Image preview modal
    openImagePreview: (
      state,
      action: PayloadAction<{
        imageType: ImagePreviewType;
        imageUrl: string | null;
      }>,
    ) => {
      state.imagePreviewModal = {
        open: true,
        imageType: action.payload.imageType,
        imageUrl: action.payload.imageUrl,
      };
    },
    closeImagePreview: (state) => {
      state.imagePreviewModal = {
        open: false,
        imageType: null,
        imageUrl: null,
      };
    },
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  setStats,
  setProducts,
  setProductsPagination,
  openDetailModal,
  closeDetailModal,
  setSelectedProduct,
  setSelectedProductLoading,
  setSelectedProductError,
  openFormModal,
  closeFormModal,
  openCategoriesModal,
  closeCategoriesModal,
  setCategories,
  addCategory,
  updateCategory,
  removeCategory,
  openImagePreview,
  closeImagePreview,
} = centralProductCatalogSlice.actions;

export default centralProductCatalogSlice.reducer;
